<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-4xl mx-auto">
        <a href="/" class="text-emerald-200 hover:text-white text-sm">&larr; All Units</a>
        <h1 class="text-2xl font-bold mt-1" v-if="unit">{{ unit.title }}</h1>
        <p class="text-emerald-100 text-sm mt-1" v-if="unit">Level {{ unit.level }} &middot; Unit {{ unit.position }}</p>
      </div>
    </header>

    <main class="max-w-4xl mx-auto px-4 py-6">
      <div v-if="loading" class="text-center py-12 text-gray-500">Loading lesson...</div>
      <div v-else-if="error" class="text-center py-12 text-red-500">{{ error }}</div>
      <div v-else>
        <!-- Mode toggle -->
        <div class="flex justify-end mb-4">
          <div class="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              @click="mode = 'learn'"
              :class="[
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                mode === 'learn' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              ]"
            >
              Learn
            </button>
            <button
              @click="mode = 'browse'"
              :class="[
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                mode === 'browse' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              ]"
            >
              Browse
            </button>
          </div>
        </div>

        <!-- Learn mode -->
        <LearnMode
          v-if="mode === 'learn'"
          :vocab="vocab"
          :grammar="grammar"
          :practiceUrl="practiceUrl"
          :unitId="unitId"
          :level="unit?.level || 1"
          :revisit="unitLearned"
          @browse="mode = 'browse'"
        />

        <!-- Browse mode -->
        <div v-else>
          <!-- Tabs -->
          <div class="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
            <button
              @click="activeTab = 'vocab'"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                activeTab === 'vocab' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              ]"
            >
              Vocabulary ({{ vocab.length }})
            </button>
            <button
              @click="activeTab = 'grammar'"
              :class="[
                'px-4 py-2 rounded-md text-sm font-medium transition-colors',
                activeTab === 'grammar' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              ]"
            >
              Grammar ({{ grammar.length }})
            </button>
          </div>

          <!-- Vocabulary Tab -->
          <div v-if="activeTab === 'vocab'" class="space-y-3">
            <VocabCard v-for="word in vocab" :key="word.id" :word="word" />
          </div>

          <!-- Grammar Tab -->
          <div v-if="activeTab === 'grammar'" class="space-y-4">
            <GrammarCard v-for="concept in grammar" :key="concept.id" :concept="concept" />
          </div>

          <!-- Action Buttons -->
          <div class="mt-8 text-center flex gap-4 justify-center">
            <a
              :href="practiceUrl"
              class="inline-block bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
            >
              Practice This Unit
            </a>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { apiGet } from '../api/client'
import VocabCard from './VocabCard.vue'
import GrammarCard from './GrammarCard.vue'
import LearnMode from './LearnMode.vue'

const props = defineProps({
  unitId: { type: Number, required: true },
  unitUrl: { type: String, required: true },
  vocabUrl: { type: String, required: true },
  grammarUrl: { type: String, required: true },
  practiceUrl: { type: String, required: true },
})

const unit = ref(null)
const vocab = ref([])
const grammar = ref([])
const activeTab = ref('vocab')
const mode = ref('learn')
const loading = ref(true)
const error = ref(null)
// Whether this unit's walkthrough was completed before. Drives LearnMode's
// revisit behavior (skip the intro, recall-style cards instead of auto-reveal).
const unitLearned = ref(false)

onMounted(async () => {
  try {
    const [unitData, vocabData, grammarData, progressData] = await Promise.all([
      apiGet(props.unitUrl),
      apiGet(props.vocabUrl),
      apiGet(props.grammarUrl),
      // Progress is a nice-to-have; never let it block the lesson.
      apiGet('/api/v1/progress').catch(() => []),
    ])
    unit.value = unitData
    vocab.value = vocabData
    grammar.value = grammarData
    unitLearned.value = !!(Array.isArray(progressData) && progressData.find(p => p.unit_id === props.unitId)?.learned)
  } catch (e) {
    error.value = 'Failed to load lesson'
  } finally {
    loading.value = false
  }
})
</script>
